import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { whiteLabelService } from '../../services/whiteLabelService';

// ─── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ icon, title, description, badge = undefined, children }) => (
  <div className="bg-card border border-border rounded-lg overflow-hidden">
    <div className="px-6 py-5 border-b border-border flex items-start gap-4">
      <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon name={icon} size={18} color="var(--color-primary)" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-heading font-semibold text-foreground">{title}</h2>
          {badge && (
            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-primary/10 text-primary">
              {badge}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

// ─── Asset uploader ────────────────────────────────────────────────────────────
const AssetUploader = ({ label, hint, currentUrl, onUpload, accept = 'image/*', uploading }) => {
  const inputRef = useRef(null);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl ? (
            <img src={currentUrl} alt={label} className="w-full h-full object-contain p-1" />
          ) : (
            <Icon name="ImagePlus" size={22} color="var(--color-muted-foreground)" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => e?.target?.files?.[0] && onUpload(e?.target?.files?.[0])}
          />
          <Button
            variant="outline"
            size="sm"
            iconName={uploading ? 'Loader2' : 'Upload'}
            iconPosition="left"
            onClick={() => inputRef?.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : currentUrl ? 'Replace' : 'Upload'}
          </Button>
          {currentUrl && (
            <p className="text-xs text-muted-foreground mt-1.5 truncate max-w-xs">{currentUrl}</p>
          )}
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

// ─── CNAME status badge ────────────────────────────────────────────────────────
const CnameBadge = ({ verified }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      verified ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
    }`}
  >
    <Icon name={verified ? 'CheckCircle2' : 'Clock'} size={12} />
    {verified ? 'Verified' : 'Pending verification'}
  </span>
);

// ─── Main page ─────────────────────────────────────────────────────────────────
const WhiteLabelBrandingPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [settings, setSettings] = useState({
    isEnabled: false,
    customDomain: '',
    cnameVerified: false,
    logoUrl: '',
    faviconUrl: '',
    primaryColor: '#107570',
    emailFooterText: '',
    agencyName: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await whiteLabelService?.getSettings();
        if (data) {
          setSettings({
            isEnabled: data?.isEnabled ?? false,
            customDomain: data?.customDomain ?? '',
            cnameVerified: data?.cnameVerified ?? false,
            logoUrl: data?.logoUrl ?? '',
            faviconUrl: data?.faviconUrl ?? '',
            primaryColor: data?.primaryColor ?? '#107570',
            emailFooterText: data?.emailFooterText ?? '',
            agencyName: data?.agencyName ?? '',
          });
        }
      } catch (err) {
        console.error('Failed to load white-label settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await whiteLabelService?.saveSettings(settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file, type) => {
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);
    try {
      const url = await whiteLabelService?.uploadAsset(file, type);
      handleChange(type === 'logo' ? 'logoUrl' : 'faviconUrl', url);
    } catch (err) {
      setError(`Failed to upload ${type}: ${err?.message}`);
    } finally {
      setUploading(false);
    }
  };

  const cnameTarget = settings?.customDomain
    ? `${settings?.customDomain} → forms.webforms.biz`
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar stats={{}}>
          <div className="flex items-center justify-center h-64">
            <Icon name="Loader2" size={28} color="var(--color-primary)" className="animate-spin" />
          </div>
        </NavigationBar>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>White-Label Branding — WebForms</title>
        <meta name="description" content="Configure custom domain, brand assets, and white-label settings for your agency portal" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <NavigationBar stats={{}}>
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">

            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <Icon name="Building2" size={14} />
                <span>Agency Portal</span>
                <Icon name="ChevronRight" size={14} />
                <span className="text-foreground font-medium">White-Label Branding</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                    White-Label Branding
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground mt-1">
                    Rebrand the platform for your clients — custom domain, logo, colors, and email footer.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {saveSuccess && (
                    <span className="flex items-center gap-1.5 text-sm text-success font-medium">
                      <Icon name="CheckCircle2" size={16} />
                      Saved
                    </span>
                  )}
                  <Button
                    variant="primary"
                    size="default"
                    iconName={saving ? 'Loader2' : 'Save'}
                    iconPosition="left"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-3 px-4 py-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
                <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-6">

              {/* ── White-label toggle ── */}
              <Section
                icon="Layers"
                title="White-Label Mode"
                description="When enabled, all WebForms.biz references are removed from embed codes, hosted form links, and customer-facing footers."
                badge={settings?.isEnabled ? 'Active' : undefined}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Enable white-label mode</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Removes all WebForms.biz branding from client-facing surfaces
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange('isEnabled', !settings?.isEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      settings?.isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                    aria-label="Toggle white-label mode"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        settings?.isEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {settings?.isEnabled && (
                  <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-md">
                    <p className="text-xs text-primary font-medium flex items-center gap-1.5">
                      <Icon name="Info" size={13} />
                      White-label is active. Embed codes and hosted form links will use your custom domain.
                    </p>
                  </div>
                )}
              </Section>

              {/* ── Custom Domain (CNAME) ── */}
              <Section
                icon="Globe"
                title="Custom Domain (CNAME)"
                description="Map your own subdomain so forms are served from your agency's domain."
              >
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      Custom subdomain
                    </label>
                    <div className="flex gap-3">
                      <Input
                        type="text"
                        placeholder="forms.youragency.com"
                        value={settings?.customDomain}
                        onChange={(e) => handleChange('customDomain', e?.target?.value)}
                        className="flex-1"
                      />
                      {settings?.customDomain && (
                        <CnameBadge verified={settings?.cnameVerified} />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter the subdomain you want to use (e.g. <code className="bg-muted px-1 rounded text-xs">forms.youragency.com</code>)
                    </p>
                  </div>

                  {settings?.customDomain && (
                    <div className="bg-muted rounded-md p-4 space-y-3">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
                        DNS Configuration Required
                      </p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-muted-foreground font-medium">Type</div>
                        <div className="text-muted-foreground font-medium">Name</div>
                        <div className="text-muted-foreground font-medium">Value</div>
                        <div className="font-mono text-foreground bg-card border border-border rounded px-2 py-1">CNAME</div>
                        <div className="font-mono text-foreground bg-card border border-border rounded px-2 py-1 truncate">
                          {settings?.customDomain?.split('.')?.[0] || '@'}
                        </div>
                        <div className="font-mono text-foreground bg-card border border-border rounded px-2 py-1 truncate">
                          forms.webforms.biz
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Add this CNAME record to your DNS provider. Propagation can take up to 48 hours.
                      </p>
                    </div>
                  )}
                </div>
              </Section>

              {/* ── Brand Assets ── */}
              <Section
                icon="Palette"
                title="Brand Assets"
                description="Upload your agency logo and favicon to replace WebForms.biz branding."
              >
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <AssetUploader
                      label="Agency Logo"
                      hint="PNG, JPG or WebP · Max 2 MB · Recommended 200×60px"
                      currentUrl={settings?.logoUrl}
                      onUpload={(file) => handleUpload(file, 'logo')}
                      uploading={uploadingLogo}
                    />
                    <AssetUploader
                      label="Favicon"
                      hint="ICO, PNG or WebP · Max 2 MB · 32×32px or 64×64px"
                      currentUrl={settings?.faviconUrl}
                      onUpload={(file) => handleUpload(file, 'favicon')}
                      accept=".ico,.png,.webp,.gif"
                      uploading={uploadingFavicon}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">Primary Theme Color</label>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          type="color"
                          value={settings?.primaryColor}
                          onChange={(e) => handleChange('primaryColor', e?.target?.value)}
                          className="w-10 h-10 rounded-md border border-border cursor-pointer bg-transparent p-0.5"
                          aria-label="Pick primary color"
                        />
                      </div>
                      <Input
                        type="text"
                        value={settings?.primaryColor}
                        onChange={(e) => handleChange('primaryColor', e?.target?.value)}
                        placeholder="#107570"
                        className="w-36 font-mono text-sm"
                        maxLength={7}
                      />
                      <div
                        className="w-8 h-8 rounded-md border border-border flex-shrink-0"
                        style={{ backgroundColor: settings?.primaryColor }}
                        aria-hidden="true"
                      />
                      <p className="text-xs text-muted-foreground">
                        Applied to buttons, links, and accents on client-facing forms
                      </p>
                    </div>
                  </div>
                </div>
              </Section>

              {/* ── Email Footer ── */}
              <Section
                icon="Mail"
                title="Custom Email Footer"
                description="Replace the default WebForms.biz footer in all outgoing notification emails."
              >
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">Agency Name</label>
                    <Input
                      type="text"
                      placeholder="Acme Agency"
                      value={settings?.agencyName}
                      onChange={(e) => handleChange('agencyName', e?.target?.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Used in the default footer text: "Powered by [Agency Name]"
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      Custom Footer Text
                      <span className="ml-1.5 text-xs text-muted-foreground font-normal">(optional — overrides default)</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder={`Powered by ${settings?.agencyName || 'Your Agency'} · Unsubscribe`}
                      value={settings?.emailFooterText}
                      onChange={(e) => handleChange('emailFooterText', e?.target?.value)}
                      className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-colors"
                    />
                    <p className="text-xs text-muted-foreground">
                      Plain text only. Leave blank to use "Powered by [Agency Name]".
                    </p>
                  </div>

                  {/* Preview */}
                  <div className="border border-border rounded-md overflow-hidden">
                    <div className="px-4 py-2 bg-muted border-b border-border flex items-center gap-2">
                      <Icon name="Eye" size={14} color="var(--color-muted-foreground)" />
                      <span className="text-xs font-medium text-muted-foreground">Footer Preview</span>
                    </div>
                    <div className="px-4 py-4 bg-card text-center">
                      <p className="text-xs text-muted-foreground">
                        {settings?.emailFooterText ||
                          (settings?.agencyName
                            ? `Powered by ${settings?.agencyName}`
                            : 'Powered by WebForms.biz')}
                      </p>
                    </div>
                  </div>
                </div>
              </Section>

              {/* ── Embed code preview ── */}
              {settings?.isEnabled && settings?.customDomain && (
                <Section
                  icon="Code2"
                  title="White-Label Embed Preview"
                  description="When white-label mode is active, embed codes use your custom domain instead of WebForms.biz."
                >
                  <div className="space-y-3">
                    <div className="relative bg-muted rounded-md overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                        <span className="text-xs font-mono font-medium text-yellow-500">JAVASCRIPT</span>
                      </div>
                      <div className="p-4 overflow-x-auto">
                        <pre className="text-xs font-mono text-foreground whitespace-pre">{`<script src="https://${settings?.customDomain}/embed.js"></script>
<script>
  Forms.embed({
    formId: 'wf_abc123xyz',
    container: '#form-container',
  });
</script>
<div id="form-container"></div>`}</pre>
                      </div>
                    </div>
                    <div className="relative bg-muted rounded-md overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                        <span className="text-xs font-mono font-medium text-orange-500">HTML (iFrame)</span>
                      </div>
                      <div className="p-4 overflow-x-auto">
                        <pre className="text-xs font-mono text-foreground whitespace-pre">{`<iframe
  src="https://${settings?.customDomain}/f/wf_abc123xyz"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Contact Form">
</iframe>`}</pre>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Icon name="Info" size={12} />
                      These codes are shown to your clients in the Embed Settings page when white-label mode is active.
                    </p>
                  </div>
                </Section>
              )}

            </div>

            {/* Bottom save bar */}
            <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Changes apply immediately to all client-facing surfaces.
              </p>
              <div className="flex items-center gap-3">
                {saveSuccess && (
                  <span className="flex items-center gap-1.5 text-sm text-success font-medium">
                    <Icon name="CheckCircle2" size={16} />
                    Saved
                  </span>
                )}
                <Button
                  variant="primary"
                  size="default"
                  iconName={saving ? 'Loader2' : 'Save'}
                  iconPosition="left"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>

          </div>
        </NavigationBar>
      </div>
    </>
  );
};

export default WhiteLabelBrandingPage;
